import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './MerchantDashboard.css';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  RefreshCw,
  Clock,
  DollarSign,
  CheckCircle,
  TrendingUp,
  ShoppingBag,
  BarChart3,
  Star
} from 'lucide-react';
import {
  getMerchantOrders,
  updateOrderStatus,
  getMerchantMetrics,
  MERCHANT_ORDER_STATUS,
  getNextStatus,
  confirmOrderPickup,
  generateMerchantCashReturnCode,
  listOnlineCouriers,
  assignCourier
} from '@/services/merchant';
import TrackingMap from '@/components/TrackingMap';
import { getRouteDirections, geocodeAddress } from '@/services/routes';
import { createAbortableQuery, isAbortError } from '@/lib/supabaseAbort';

const ROUTE_REFRESH_MS = Number(process.env.REACT_APP_ROUTE_REFRESH_MS) || 30000;
const GEOCODE_REFRESH_MS = Number(process.env.REACT_APP_GEOCODE_REFRESH_MS) || 15000;
const MERCHANT_ORDERS_POLL_MS = Number(process.env.REACT_APP_MERCHANT_ORDERS_POLL_MS) || 4000;
const MERCHANT_ORDERS_POLL_HIDDEN_MS = Math.max(MERCHANT_ORDERS_POLL_MS * 4, 15000);
const PAYMENT_METHOD_LABELS = {
  credit_card: 'Cartao de credito',
  debit_card: 'Cartao de debito',
  pix: 'PIX',
  cash: 'Dinheiro',
  voucher: 'Voucher',
  card: 'Cartao',
};
const ORDER_PAYMENT_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos pagamentos' },
  { value: 'cash', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'card', label: 'Cartao' },
  { value: 'voucher', label: 'Voucher' },
];
const ORDER_SORT_OPTIONS = [
  { value: 'recent', label: 'Mais recentes' },
  { value: 'oldest', label: 'Mais antigos' },
  { value: 'highest', label: 'Maior valor' },
  { value: 'lowest', label: 'Menor valor' },
];
const ONLINE_PAYMENT_METHODS = new Set(['pix', 'credit_card', 'debit_card', 'card', 'voucher']);
const ONLINE_PAYMENT_REQUIRED_ORDER_STATUSES = new Set([
  'confirmed',
  'preparing',
  'ready',
  'awaiting_pickup',
  'out_for_delivery',
  'delivered',
]);
const MERCHANT_WAITING_FINANCIAL_LABEL = 'Aguardando transacao financeira';

function isDocumentVisible() {
  if (typeof document === 'undefined') return true;
  return document.visibilityState === 'visible';
}

function normalizeSearchToken(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function formatVoucherIssuer(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'ticket') return 'Ticket';
  if (key === 'pluxee') return 'Pluxee';
  if (key === 'biq') return 'Biq';
  if (key === 'unknown') return 'Desconhecido';
  return value || '-';
}

function formatVoucherType(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'alimentacao') return 'Alimentacao';
  if (key === 'refeicao') return 'Refeicao';
  if (key === 'unknown') return 'Desconhecido';
  return value || '-';
}

function formatCountdownMmSs(totalSeconds) {
  const safeSeconds = Math.max(0, Math.trunc(Number(totalSeconds) || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function normalizePaymentMethod(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizePaymentStatus(value) {
  return String(value || '').trim().toLowerCase();
}

function isPickupOrder(order) {
  if (!order || typeof order !== 'object') return false;
  const fulfillmentType = String(order?.fulfillment_type || 'delivery').trim().toLowerCase();
  const addressType = String(order?.delivery_address?.type || '').trim().toLowerCase();
  return fulfillmentType === 'pickup' || addressType === 'pickup';
}

function isOnlinePaymentMethod(value) {
  return ONLINE_PAYMENT_METHODS.has(normalizePaymentMethod(value));
}

function isOrderAwaitingFinancialSettlement(order) {
  if (!order || typeof order !== 'object') return false;
  const status = String(order.status || '').trim().toLowerCase();
  if (status !== 'pending') return false;
  if (!isOnlinePaymentMethod(order.payment_method)) return false;
  return normalizePaymentStatus(order.payment_status) !== 'approved';
}

function canAdvanceOrderStatus(order) {
  if (!order || typeof order !== 'object') return false;
  const nextStatus = getNextStatus(order.status);
  if (!nextStatus) return false;
  if (!ONLINE_PAYMENT_REQUIRED_ORDER_STATUSES.has(String(nextStatus || '').trim().toLowerCase())) {
    return true;
  }
  if (!isOnlinePaymentMethod(order.payment_method)) {
    return true;
  }
  return normalizePaymentStatus(order.payment_status) === 'approved';
}

function getMerchantOrderStatusLabel(order) {
  if (isOrderAwaitingFinancialSettlement(order)) {
    return MERCHANT_WAITING_FINANCIAL_LABEL;
  }
  const status = String(order?.status || '').trim().toLowerCase();
  return MERCHANT_ORDER_STATUS[status]?.label || status || '-';
}

function getMerchantPaymentStatusLabel(order) {
  const status = normalizePaymentStatus(order?.payment_status);
  if (!status) return '-';
  if (status === 'approved') return 'Aprovado';
  if (status === 'pending') return 'Aguardando pagamento';
  if (status === 'pending_cash') return 'Pagamento na entrega (dinheiro)';
  if (status === 'in_process') return 'Em processamento';
  if (status === 'rejected') return 'Recusado';
  if (status === 'cancelled') return 'Cancelado';
  if (status === 'pending_error') return 'Falha tecnica temporaria';
  if (status === 'technical_failure') return 'Falha tecnica';
  return status;
}

function getCashReturnStatusLabel(value) {
  const status = String(value || '').trim().toLowerCase();
  if (status === 'overdue') return 'Vencida';
  if (status === 'code_generated') return 'Codigo gerado';
  if (status === 'settled') return 'Devolucao concluida';
  if (status === 'cancelled') return 'Cancelada';
  return 'Pendente';
}

export default function MerchantDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [orderPaymentFilter, setOrderPaymentFilter] = useState('all');
  const [orderSortMode, setOrderSortMode] = useState('recent');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [pickupCode, setPickupCode] = useState('');
  const [couriers, setCouriers] = useState([]);
  const [loadingCouriers, setLoadingCouriers] = useState(false);
  const [selectedCourierId, setSelectedCourierId] = useState('');
  const [assigningCourier, setAssigningCourier] = useState(false);

  const [confirmingPickup, setConfirmingPickup] = useState(false);
  const [pickupCodeError, setPickupCodeError] = useState('');
  const [routeData, setRouteData] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState('');
  const [resolvedCustomerCoords, setResolvedCustomerCoords] = useState(null);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [geocodeError, setGeocodeError] = useState('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelTargetOrderId, setCancelTargetOrderId] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [timingNowMs, setTimingNowMs] = useState(() => Date.now());
  const [cashReturnCodeByOrderId, setCashReturnCodeByOrderId] = useState({});
  const [generatingCashReturnCodeId, setGeneratingCashReturnCodeId] = useState(null);

  const abortQueryRef = useRef(null);
  const routeRequestRef = useRef({ key: '', ts: 0 });
  const geocodeRequestRef = useRef({ key: '', ts: 0 });

  const loadOrders = useCallback(async () => {
    if (abortQueryRef.current) {
      abortQueryRef.current.abort();
    }

    abortQueryRef.current = createAbortableQuery();
    const query = abortQueryRef.current;

    setLoadingOrders(true);
    try {
      const data = await query.execute(async () => {
        return await getMerchantOrders(null);
      });

      if (query.isAborted()) return;

      const nextOrders = Array.isArray(data) ? data : [];
      setOrders(nextOrders);
      setSelectedOrder((prev) => {
        if (!prev?.id) return prev;
        const fresh = nextOrders.find((item) => item.id === prev.id);
        return fresh || prev;
      });
      return nextOrders;
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }

      if (!query.isAborted()) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: err.message || "Nao foi possivel carregar os pedidos.",
        });
      }
    } finally {
      if (!query.isAborted()) {
        setLoadingOrders(false);
      }
    }
  }, [toast]);

  
  const loadCouriers = useCallback(async (coords = null) => {
    setLoadingCouriers(true);
    try {
      const params = coords && coords.lat != null && coords.lng != null
        ? { lat: coords.lat, lng: coords.lng }
        : {};
      const data = await listOnlineCouriers(params);
      setCouriers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: err.message || "Nao foi possivel carregar motoboys.",
      });
    } finally {
      setLoadingCouriers(false);
    }
  }, [toast]);

const loadMetrics = useCallback(async () => {
    setLoadingMetrics(true);
    try {
      const data = await getMerchantMetrics();
      setMetrics(data);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: err.message || "Nao foi possivel carregar as metricas.",
      });
    } finally {
      setLoadingMetrics(false);
    }
  }, [toast]);

  useEffect(() => {
    const init = async () => {
      if (!user) {
        setLoadingOrders(false);
        setLoadingMetrics(false);
        return;
      }

      await Promise.all([loadOrders(), loadMetrics(), loadCouriers()]);
    };

    init();

    return () => {
      if (abortQueryRef.current) {
        abortQueryRef.current.abort();
      }
    };
  }, [user, loadOrders, loadMetrics, loadCouriers]);

  useEffect(() => {
    if (!user) return;

    let stopped = false;
    let inFlight = false;
    let timeoutId = null;

    const schedule = () => {
      if (stopped) return;
      const delay = isDocumentVisible() ? MERCHANT_ORDERS_POLL_MS : MERCHANT_ORDERS_POLL_HIDDEN_MS;
      timeoutId = window.setTimeout(tick, delay);
    };

    const tick = async () => {
      if (stopped) return;

      if (!isDocumentVisible() || inFlight) {
        schedule();
        return;
      }

      inFlight = true;
      try {
        await loadOrders();
      } finally {
        inFlight = false;
        schedule();
      }
    };

    schedule();
    return () => {
      stopped = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [user, loadOrders]);

  const rawCustomerCoords = useMemo(() => {
    const address = selectedOrder?.delivery_address || {};
    const lat = toNumber(address.lat ?? address.latitude);
    const lng = toNumber(address.lng ?? address.longitude);
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }, [selectedOrder]);

  const restaurantCoords = useMemo(() => {
    const restaurant = selectedOrder?.restaurant || {};
    const address = selectedOrder?.delivery_address || {};
    const lat = toNumber(restaurant.lat ?? address.restaurant_lat);
    const lng = toNumber(restaurant.lng ?? address.restaurant_lng);
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }, [selectedOrder]);

  const selectedOrderDeliverySettings = useMemo(() => {
    const settings = selectedOrder?.restaurant?.delivery_settings;
    if (!settings || typeof settings !== 'object') {
      return {
        courier_mode: 'own',
        allow_platform_on_demand: false,
      };
    }
    return {
      courier_mode: String(settings.courier_mode || 'own').toLowerCase() === 'own' ? 'own' : 'platform',
      allow_platform_on_demand: Boolean(settings.allow_platform_on_demand),
    };
  }, [selectedOrder]);

  const canCallPlatformCourier = selectedOrderDeliverySettings.courier_mode === 'platform'
    || selectedOrderDeliverySettings.allow_platform_on_demand;

  useEffect(() => {
    if (!selectedOrder || selectedOrder.status !== 'ready') {
      return;
    }
    if (!canCallPlatformCourier) {
      setCouriers([]);
      return;
    }
    if (!restaurantCoords) {
      setCouriers([]);
      return;
    }
    loadCouriers(restaurantCoords);
  }, [selectedOrder, restaurantCoords, loadCouriers, canCallPlatformCourier]);

  useEffect(() => {
    const deadline = String(selectedOrder?.courier_timing?.deadline_at || '').trim();
    if (!deadline) return undefined;
    const intervalId = window.setInterval(() => {
      setTimingNowMs(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [selectedOrder?.courier_timing?.deadline_at]);

  useEffect(() => {
    if (!selectedOrder) {
      setResolvedCustomerCoords(null);
      setGeocodeError('');
      return;
    }

    if (rawCustomerCoords) {
      setResolvedCustomerCoords(rawCustomerCoords);
      setGeocodeError('');
      return;
    }

    const address = selectedOrder?.delivery_address || {};
    const fulfillmentType = (selectedOrder?.fulfillment_type || 'delivery').toLowerCase();
    if (fulfillmentType === 'pickup' || address.type === 'pickup') {
      setResolvedCustomerCoords(null);
      setGeocodeError('');
      return;
    }

    const textParts = [address.label, address.address, address.full_address, address.street, address.number, address.neighborhood, address.city, address.state, address.zipcode]
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);
    const text = textParts.join(', ');

    if (!text) {
      setResolvedCustomerCoords(null);
      setGeocodeError('');
      return;
    }

    const key = text.toLowerCase();
    const now = Date.now();
    if (geocodeRequestRef.current.key === key && (now - geocodeRequestRef.current.ts) < GEOCODE_REFRESH_MS) {
      return;
    }

    geocodeRequestRef.current = { key, ts: now };
    setGeocodeLoading(true);
    setGeocodeError('');

    geocodeAddress({ text })
      .then((data) => {
        const lat = Number(data?.lat);
        const lng = Number(data?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setResolvedCustomerCoords({ lat, lng });
        } else {
          setResolvedCustomerCoords(null);
          setGeocodeError('Nao foi possivel localizar o endereco');
        }
      })
      .catch((err) => {
        setResolvedCustomerCoords(null);
        setGeocodeError(err?.message || 'Nao foi possivel localizar o endereco');
      })
      .finally(() => {
        setGeocodeLoading(false);
      });
  }, [selectedOrder, rawCustomerCoords]);

  useEffect(() => {
    setPickupCodeError('');
    setPickupCode('');
  }, [selectedOrder?.id]);

  const customerCoords = resolvedCustomerCoords;

  useEffect(() => {
    if (!selectedOrder || !restaurantCoords || !customerCoords) {
      setRouteData(null);
      setRouteError('');
      return;
    }

    const key = `${restaurantCoords.lat},${restaurantCoords.lng}->${customerCoords.lat},${customerCoords.lng}`;
    const now = Date.now();
    if (routeRequestRef.current.key === key && (now - routeRequestRef.current.ts) < ROUTE_REFRESH_MS) {
      return;
    }

    routeRequestRef.current = { key, ts: now };
    setRouteLoading(true);
    setRouteError('');

    getRouteDirections({ start: restaurantCoords, end: customerCoords })
      .then((data) => {
        setRouteData(data || null);
      })
      .catch((err) => {
        setRouteData(null);
        setRouteError(err?.message || 'Nao foi possivel carregar a rota');
      })
      .finally(() => {
        setRouteLoading(false);
      });
  }, [selectedOrder?.id, restaurantCoords?.lat, restaurantCoords?.lng, customerCoords?.lat, customerCoords?.lng]);

  const handleUpdateStatus = async (orderId, newStatus) => {
    const updateQuery = createAbortableQuery();

    setUpdatingStatus(true);
    try {
      await updateQuery.execute(async () => {
        return await updateOrderStatus(orderId, newStatus);
      });

      if (updateQuery.isAborted()) return;

      toast({
        title: "Status atualizado!",
        description: `Pedido marcado como ${MERCHANT_ORDER_STATUS[newStatus]?.label || newStatus}`,
      });

      await loadOrders();

      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }

      if (!updateQuery.isAborted()) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: err.message || "Nao foi possivel atualizar o status.",
        });
      }
    } finally {
      if (!updateQuery.isAborted()) {
        setUpdatingStatus(false);
      }
    }
  };

  const handleRefresh = async () => {
    const courierCoords = selectedOrder?.status === 'ready' && restaurantCoords
      ? restaurantCoords
      : null;
    await Promise.all([loadOrders(), loadMetrics(), loadCouriers(courierCoords)]);
  };

  
  const handleAssignCourier = async (orderId) => {
    if (!selectedCourierId) {
      toast({
        variant: 'destructive',
        title: 'Selecione um motoboy',
        description: 'Escolha um motoboy para atribuir o pedido.',
      });
      return;
    }

    setAssigningCourier(true);
    try {
      await assignCourier(orderId, selectedCourierId);
      toast({
        title: 'Motoboy atribuido',
        description: 'Pedido enviado para aceite do motoboy.',
      });
      setSelectedCourierId('');
      const updatedOrders = await loadOrders();
      if (selectedOrder?.id === orderId) {
        const fresh = (updatedOrders || []).find((item) => item.id === orderId);
        setSelectedOrder(fresh || selectedOrder);
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: err.message || 'Nao foi possivel atribuir o motoboy.',
      });
    } finally {
      setAssigningCourier(false);
    }
  };

const handleConfirmPickup = async (orderId) => {
    const code = pickupCode.trim();
    const isPickupOrder = (
      (selectedOrder?.fulfillment_type || 'delivery').toLowerCase() === 'pickup'
      || selectedOrder?.delivery_address?.type === 'pickup'
    );
    setPickupCodeError('');
    if (!code) {
      setPickupCodeError('Numero obrigatorio.');
      toast({
        variant: 'destructive',
        title: 'Codigo obrigatorio',
        description: isPickupOrder
          ? 'Informe o codigo de retirada informado pelo cliente.'
          : 'Informe o codigo do pedido para confirmar a retirada.',
      });
      return;
    }

    setConfirmingPickup(true);
    try {
      const result = await confirmOrderPickup(orderId, code);
      toast({
        title: 'Retirada confirmada',
        description: isPickupOrder
          ? 'Pedido retirado e finalizado com sucesso.'
          : 'Pedido liberado para entrega.',
      });
      setPickupCode('');
      setPickupCodeError('');
      const updatedOrders = await loadOrders();
      if (selectedOrder?.id === orderId) {
        const fresh = (updatedOrders || []).find((item) => item.id === orderId);
        const fallbackStatus = result?.status || (isPickupOrder ? 'delivered' : 'out_for_delivery');
        setSelectedOrder(fresh || ((prev) => ({ ...prev, status: fallbackStatus })));
      }
    } catch (err) {
      const rawMessage = String(err?.message || '').toLowerCase();
      const isWrongCode = rawMessage.includes('invalido')
        || rawMessage.includes('incorreto')
        || rawMessage.includes('codigo do cliente');
      const inlineMessage = isWrongCode
        ? 'Codigo incorreto. Tente novamente.'
        : (err.message || 'Nao foi possivel confirmar a retirada.');
      setPickupCodeError(inlineMessage);
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: inlineMessage,
      });
    } finally {
      setConfirmingPickup(false);
    }
  };

  const handleGenerateCashReturnCode = async (orderId) => {
    const orderIdStr = String(orderId || '').trim();
    if (!orderIdStr) return;

    setGeneratingCashReturnCodeId(orderIdStr);
    try {
      const result = await generateMerchantCashReturnCode(orderIdStr);
      const nextCashReturn = result?.cash_return && typeof result.cash_return === 'object'
        ? result.cash_return
        : null;
      const code = String(result?.confirmation_code || '').trim();

      if (code) {
        setCashReturnCodeByOrderId((prev) => ({ ...prev, [orderIdStr]: code }));
      }

      if (nextCashReturn) {
        setOrders((prev) => (
          Array.isArray(prev)
            ? prev.map((item) => (String(item?.id) === orderIdStr ? { ...item, cash_return: nextCashReturn } : item))
            : prev
        ));
        setSelectedOrder((prev) => (
          prev && String(prev?.id) === orderIdStr
            ? { ...prev, cash_return: nextCashReturn }
            : prev
        ));
      }

      toast({
        title: 'Codigo gerado',
        description: 'Compartilhe este codigo com o motoboy para confirmar a devolucao do dinheiro.',
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: err?.message || 'Nao foi possivel gerar codigo de devolucao.',
      });
    } finally {
      setGeneratingCashReturnCodeId(null);
    }
  };

  const handleCancelOrder = (orderId) => {
    setCancelTargetOrderId(orderId);
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    if (cancelSubmitting) return;
    setCancelModalOpen(false);
    setCancelTargetOrderId(null);
  };

  const confirmCancelOrder = async () => {
    if (!cancelTargetOrderId) return;
    setCancelSubmitting(true);
    try {
      await handleUpdateStatus(cancelTargetOrderId, 'cancelled');
      setCancelModalOpen(false);
      setCancelTargetOrderId(null);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    return MERCHANT_ORDER_STATUS[status]?.color || '#6b7280';
  };

  const getOrdersByStatus = (status) => {
    if (status === 'all') return orders;
    return orders.filter(order => order.status === status);
  };

  const getOrderPaymentFilterKey = (order) => {
    const raw = String(order?.payment_method || '').trim().toLowerCase();
    if (raw === 'credit_card' || raw === 'debit_card' || raw === 'card') return 'card';
    if (raw === 'pix') return 'pix';
    if (raw === 'cash') return 'cash';
    if (raw === 'voucher') return 'voucher';
    return raw || 'unknown';
  };

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    preparing: orders.filter(o => o.status === 'preparing').length,
    ready: orders.filter(o => o.status === 'ready').length,
    awaiting_pickup: orders.filter(o => o.status === 'awaiting_pickup').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  };

  const cancelledCount = metrics?.summary?.cancelled_orders ?? stats.cancelled;

  const statusOrders = useMemo(() => getOrdersByStatus(selectedStatus), [orders, selectedStatus]);
  const filteredOrders = useMemo(() => {
    const query = normalizeSearchToken(orderSearchTerm);
    const next = statusOrders.filter((order) => {
      if (orderPaymentFilter !== 'all' && getOrderPaymentFilterKey(order) !== orderPaymentFilter) {
        return false;
      }

      if (!query) return true;

      const searchable = normalizeSearchToken([
        order?.order_number,
        order?.customer?.name,
        order?.customer?.email,
        order?.delivery_address?.address,
        ...(Array.isArray(order?.order_items) ? order.order_items.map((item) => item?.product_name) : []),
      ].filter(Boolean).join(' '));
      return searchable.includes(query);
    });

    next.sort((a, b) => {
      const aTime = new Date(a?.created_at || 0).getTime();
      const bTime = new Date(b?.created_at || 0).getTime();
      const resolveComparableTotal = (order) => {
        const rawMerchant = Number(order?.merchant_total);
        if (Number.isFinite(rawMerchant)) return rawMerchant;
        const total = Number(order?.total || 0);
        const delivery = Number(order?.delivery_fee || 0);
        return Boolean(order?.delivery_using_platform) ? Math.max(0, total - delivery) : total;
      };
      const aTotal = resolveComparableTotal(a);
      const bTotal = resolveComparableTotal(b);

      if (orderSortMode === 'oldest') return aTime - bTime;
      if (orderSortMode === 'highest') return bTotal - aTotal;
      if (orderSortMode === 'lowest') return aTotal - bTotal;
      return bTime - aTime;
    });

    return next;
  }, [statusOrders, orderPaymentFilter, orderSearchTerm, orderSortMode]);

  const formatCurrency = (valueCents) => {
    const value = (valueCents || 0) / 100;
    return value.toFixed(2).replace('.', ',');
  };

  const openLogistaMetricDetail = useCallback((payload = {}) => {
    const params = new URLSearchParams();
    params.set('tab', payload.tab || 'financeiro');
    if (payload.period) params.set('period', String(payload.period));
    if (payload.status) params.set('status', String(payload.status));
    if (payload.payment) params.set('payment', String(payload.payment));
    if (payload.date_from) params.set('date_from', String(payload.date_from));
    if (payload.date_to) params.set('date_to', String(payload.date_to));
    if (payload.q) params.set('q', String(payload.q));
    navigate(`/logista?${params.toString()}`);
  }, [navigate]);

  const formatCurrencyFromReais = (valueReais) => {
    const value = Number(valueReais);
    if (!Number.isFinite(value)) return '0,00';
    return value.toFixed(2).replace('.', ',');
  };

  const resolveCashChangeData = (order) => {
    const raw = order?.cash_change;
    if (!raw || typeof raw !== 'object') return null;

    const needsChange = Boolean(raw.needs_change);
    const billAmount = Number(raw.bill_amount);
    const changeDue = Number(raw.change_due);

    return {
      needsChange,
      billAmount: Number.isFinite(billAmount) ? billAmount : null,
      changeDue: Number.isFinite(changeDue) ? changeDue : null,
    };
  };

  const resolveOrderUsesPlatformCourier = (order) => {
    if (!order || typeof order !== 'object') return false;

    if (typeof order.delivery_using_platform === 'boolean') {
      return order.delivery_using_platform;
    }

    const courierMode = String(order?.restaurant?.delivery_settings?.courier_mode || '').trim().toLowerCase();
    if (courierMode === 'platform') {
      return true;
    }

    const pricing =
      (order.delivery_pricing && typeof order.delivery_pricing === 'object' ? order.delivery_pricing : null)
      || (order.metadata?.delivery_pricing && typeof order.metadata.delivery_pricing === 'object' ? order.metadata.delivery_pricing : null)
      || (order.metadata_?.delivery_pricing && typeof order.metadata_.delivery_pricing === 'object' ? order.metadata_.delivery_pricing : null);

    if (pricing) {
      if (typeof pricing.using_platform_courier === 'boolean') {
        return pricing.using_platform_courier;
      }

      const chargedSource = String(pricing.charged_source || '').trim().toLowerCase();
      const effectiveSource = String(pricing.effective_delivery_cost_source || '').trim().toLowerCase();
      if (chargedSource === 'admin' || effectiveSource === 'admin') return true;
      if (chargedSource === 'merchant' || effectiveSource === 'merchant') return false;
    }

    return false;
  };

  const resolveMerchantTotal = (order) => {
    if (order && Number.isFinite(Number(order.merchant_total))) {
      return Number(order.merchant_total);
    }
    const baseTotal = Number(order?.total || 0);
    const deliveryFee = Number(order?.delivery_fee || 0);
    if (resolveOrderUsesPlatformCourier(order)) {
      return Math.max(0, baseTotal - deliveryFee);
    }
    return baseTotal;
  };

  const resolveMerchantDeliveryFee = (order) => {
    if (order && Number.isFinite(Number(order.merchant_delivery_fee))) {
      return Number(order.merchant_delivery_fee);
    }
    if (resolveOrderUsesPlatformCourier(order)) {
      return 0;
    }
    return Number(order?.delivery_fee || 0);
  };

  const resolveDisplayedOrderTotal = (order) => {
    const paymentMethod = String(order?.payment_method || '').trim().toLowerCase();
    if (paymentMethod === 'cash' || paymentMethod === 'dinheiro') {
      return Number(order?.total || 0);
    }
    return resolveMerchantTotal(order);
  };

  const resolveDisplayedDeliveryFee = (order) => {
    const paymentMethod = String(order?.payment_method || '').trim().toLowerCase();
    if (paymentMethod === 'cash' || paymentMethod === 'dinheiro') {
      return Number(order?.delivery_fee || 0);
    }
    return resolveMerchantDeliveryFee(order);
  };

  const resolveOrderDeliveryDistanceKm = (order) => {
    if (!order || typeof order !== 'object') return null;

    const explicitDistance = Number(order?.delivery_distance_km);
    if (Number.isFinite(explicitDistance) && explicitDistance >= 0) {
      return explicitDistance;
    }

    const pricing =
      (order.delivery_pricing && typeof order.delivery_pricing === 'object' ? order.delivery_pricing : null)
      || (order.metadata?.delivery_pricing && typeof order.metadata.delivery_pricing === 'object' ? order.metadata.delivery_pricing : null)
      || (order.metadata_?.delivery_pricing && typeof order.metadata_.delivery_pricing === 'object' ? order.metadata_.delivery_pricing : null);

    const pricingDistance = Number(pricing?.distance_km);
    if (Number.isFinite(pricingDistance) && pricingDistance >= 0) {
      return pricingDistance;
    }

    const restaurant = order?.restaurant || {};
    const address = order?.delivery_address || {};
    const storeLat = toNumber(restaurant.lat ?? address.restaurant_lat);
    const storeLng = toNumber(restaurant.lng ?? address.restaurant_lng);
    const customerLat = toNumber(address.lat ?? address.latitude);
    const customerLng = toNumber(address.lng ?? address.longitude);
    if (storeLat == null || storeLng == null || customerLat == null || customerLng == null) return null;

    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(customerLat - storeLat);
    const dLng = toRad(customerLng - storeLng);
    const a = Math.sin(dLat / 2) ** 2
      + Math.cos(toRad(storeLat)) * Math.cos(toRad(customerLat)) * Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Number((6371 * c).toFixed(3));
  };

  const formatDistanceKm = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '-';
    return `${parsed.toFixed(1).replace('.', ',')} km`;
  };

  const shouldShowCourierOnWayToStore = Boolean(selectedOrder?.courier) && (
    Boolean(selectedOrder?.courier_accepted_at) || selectedOrder?.status === 'awaiting_pickup'
  ) && !['out_for_delivery', 'delivered', 'cancelled'].includes(String(selectedOrder?.status || '').toLowerCase());

  const selectedOrderCourierTiming = useMemo(() => {
    const timing = selectedOrder?.courier_timing;
    if (!timing || typeof timing !== 'object') return null;
    const deadlineMs = new Date(String(timing.deadline_at || '')).getTime();
    if (!Number.isFinite(deadlineMs)) return null;
    const phase = String(timing.phase || '').toLowerCase() === 'delivery' ? 'delivery' : 'pickup';
    const rawRemaining = Math.floor((deadlineMs - timingNowMs) / 1000);
    return {
      phase,
      instanceLabel: String(
        timing.instance_label
        || (phase === 'delivery' ? 'Entrega do pedido ao cliente' : 'Retirada do pedido na loja')
      ),
      clockLabel: String(timing.clock_label || (phase === 'delivery' ? 'Entregar em' : 'Retirar em')),
      countdown: formatCountdownMmSs(rawRemaining),
      isOverdue: rawRemaining < 0,
    };
  }, [selectedOrder?.courier_timing, timingNowMs]);

  const selectedOrderPaymentMethod = String(selectedOrder?.payment_method || '').toLowerCase();
  const selectedOrderIsPickup = isPickupOrder(selectedOrder);
  const selectedOrderHasCourier = Boolean(selectedOrder?.courier_id || selectedOrder?.courier?.id);
  const selectedOrderCashChange = resolveCashChangeData(selectedOrder);
  const selectedOrderDistanceKm = resolveOrderDeliveryDistanceKm(selectedOrder);
  const selectedOrderCashReturn = selectedOrder?.cash_return && typeof selectedOrder.cash_return === 'object'
    ? selectedOrder.cash_return
    : null;
  const selectedOrderCashReturnStatus = String(selectedOrderCashReturn?.status || '').trim().toLowerCase();
  const selectedOrderCashReturnPending = selectedOrderPaymentMethod === 'cash'
    && selectedOrderHasCourier
    && !selectedOrderIsPickup
    && String(selectedOrder?.status || '').trim().toLowerCase() === 'delivered'
    && !['settled', 'cancelled'].includes(selectedOrderCashReturnStatus);
  const selectedOrderGeneratedCashReturnCode = selectedOrder?.id
    ? cashReturnCodeByOrderId[String(selectedOrder.id)]
    : null;

  function toNumber(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const resolveCancellationReason = (order) => {
    const source = order || {};
    return source.cancellation_reason || source.metadata?.cancellation_reason || source.metadata_?.cancellation_reason || null;
  };

  const resolveCancellationComment = (order) => {
    const source = order || {};
    return source.cancellation_comment || source.metadata?.cancellation_comment || source.metadata_?.cancellation_comment || null;
  };

  const resolveCancelledBy = (order) => {
    const source = order || {};
    const fromRoot = source.cancelled_by;
    const fromMetadata = source.metadata?.cancelled_by || source.metadata_?.cancelled_by;
    const value = fromRoot || fromMetadata;

    if (value === 'merchant') return 'Lojista';
    if (value === 'customer') return 'Cliente';

    // Fallback de compatibilidade para pedidos antigos
    if (resolveCancellationReason(source)) return 'Cliente';
    if (source.status === 'cancelled') return 'Lojista';
    return 'Nao informado';
  };

  const series = metrics?.series || [];
  const maxOrders = Math.max(...series.map(d => d.orders), 1);
  const maxDelivered = Math.max(...series.map(d => d.delivered), 1);

  const storeName = metrics?.store?.name || 'Minha Loja';

  if (loadingOrders && loadingMetrics) {
    return (
      <div className="dashboard-loading">
        <RefreshCw className="loading-spinner" size={48} />
        <p>Carregando painel...</p>
      </div>
    );
  }

  return (
    <div className="merchant-dashboard">
      <div className="dashboard-header">
        <button className="btn-back" onClick={() => navigate('/logista')}>
          <ArrowLeft size={20} />
          Voltar
        </button>
        <div className="header-content">
          <div>
            <h1>{storeName}</h1>
            <p className="header-subtitle">Dashboard do Logista</p>
          </div>
          <button className="btn-refresh" onClick={handleRefresh}>
            <RefreshCw size={18} />
            Atualizar
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <button
          type="button"
          className="stat-card stat-card-action"
          title="Abrir detalhes de pedidos totais"
          onClick={() => openLogistaMetricDetail({ tab: 'financeiro', status: 'all' })}
        >
          <div className="stat-icon" style={{ background: '#fff1e6' }}>
            <BarChart3 size={24} style={{ color: '#ff7a00' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Pedidos Totais</p>
            <p className="stat-value">{metrics?.summary?.total_orders ?? 0}</p>
          </div>
        </button>

        <button
          type="button"
          className="stat-card stat-card-action"
          title="Abrir pedidos entregues"
          onClick={() => openLogistaMetricDetail({ tab: 'financeiro', status: 'completed' })}
        >
          <div className="stat-icon" style={{ background: '#e7f7ee' }}>
            <CheckCircle size={24} style={{ color: '#10b981' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Entregues</p>
            <p className="stat-value">{metrics?.summary?.delivered_orders ?? 0}</p>
          </div>
        </button>

        <button
          type="button"
          className="stat-card stat-card-action"
          title="Abrir ticket medio do dia"
          onClick={() => openLogistaMetricDetail({ tab: 'metrica', period: 'today', status: 'all' })}
        >
          <div className="stat-icon" style={{ background: '#fff7ed' }}>
            <DollarSign size={24} style={{ color: '#ff7a00' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Ticket Medio (Dia)</p>
            <p className="stat-value">R$ {formatCurrency(metrics?.summary?.avg_ticket_day)}</p>
          </div>
        </button>

        <button
          type="button"
          className="stat-card stat-card-action"
          title="Abrir ticket medio do mes"
          onClick={() => openLogistaMetricDetail({ tab: 'metrica', period: 'month', status: 'all' })}
        >
          <div className="stat-icon" style={{ background: '#f3f0ff' }}>
            <TrendingUp size={24} style={{ color: '#7c3aed' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Ticket Medio (Mes)</p>
            <p className="stat-value">R$ {formatCurrency(metrics?.summary?.avg_ticket_month)}</p>
          </div>
        </button>

        <button
          type="button"
          className="stat-card stat-card-action highlight"
          title="Abrir faturamento do dia"
          onClick={() => openLogistaMetricDetail({ tab: 'metrica', period: 'today', status: 'all' })}
        >
          <div className="stat-icon" style={{ background: 'rgba(255,255,255,0.2)' }}>
            <DollarSign size={24} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Faturamento Hoje</p>
            <p className="stat-value">R$ {formatCurrency(metrics?.summary?.revenue_day)}</p>
          </div>
        </button>

        <button
          type="button"
          className="stat-card stat-card-action"
          title="Abrir faturamento do mes"
          onClick={() => openLogistaMetricDetail({ tab: 'metrica', period: 'month', status: 'all' })}
        >
          <div className="stat-icon" style={{ background: '#eef2ff' }}>
            <ShoppingBag size={24} style={{ color: '#4f46e5' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Faturamento Mes</p>
            <p className="stat-value">R$ {formatCurrency(metrics?.summary?.revenue_month)}</p>
          </div>
        </button>

        <button
          type="button"
          className="stat-card stat-card-action"
          title="Abrir pedidos cancelados"
          onClick={() => openLogistaMetricDetail({ tab: 'financeiro', status: 'cancelled' })}
        >
          <div className="stat-icon" style={{ background: '#fee2e2' }}>
            <Clock size={24} style={{ color: '#ef4444' }} />
          </div>
          <div className="stat-info">
            <p className="stat-label">Pedidos Cancelados</p>
            <p className="stat-value">{cancelledCount}</p>
          </div>
        </button>
      </div>

      <div className="metrics-grid">
        <div className="chart-card">
          <div className="chart-header">
            <div>
              <h3>Pedidos por Dia</h3>
              <p>Ultimos 14 dias</p>
            </div>
            <div className="chart-legend">
              <span className="legend-item"><span className="legend-dot total" />Total</span>
              <span className="legend-item"><span className="legend-dot delivered" />Entregues</span>
            </div>
          </div>
          <div className="chart-bars">
            {series.map((item) => {
              const dayLabel = new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit' });
              return (
                <div className="chart-bar" key={item.date}>
                  <div
                    className="bar total"
                    style={{ height: `${(item.orders / maxOrders) * 100}%` }}
                    title={`Pedidos: ${item.orders}`}
                  />
                  <div
                    className="bar delivered"
                    style={{ height: `${(item.delivered / maxDelivered) * 100}%` }}
                    title={`Entregues: ${item.delivered}`}
                  />
                  <span className="bar-label">{dayLabel}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="top-products-card">
          <div className="top-products-header">
            <h3>Produtos Mais Pedidos</h3>
            <p>Baseado nos ultimos pedidos</p>
          </div>
          <div className="top-products-list">
            {(metrics?.top_products || []).length === 0 ? (
              <div className="empty-metrics">
                <Star size={32} />
                <p>Nenhum produto registrado ainda</p>
              </div>
            ) : (
              metrics.top_products.map((product, index) => (
                <div key={product.product_name} className="top-product-item">
                  <div className="product-rank">{index + 1}</div>
                  <div className="product-info">
                    <p className="product-name">{product.product_name}</p>
                    <p className="product-meta">{product.quantity} pedidos</p>
                  </div>
                  <div className="product-revenue">R$ {formatCurrency(product.revenue)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="status-filters">
        <button
          className={`merchant-filter-btn ${selectedStatus === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedStatus('all')}
        >
          <span className="merchant-filter-label">Todos</span>
          <span className="merchant-filter-count">({orders.length})</span>
        </button>
        <button
          className={`merchant-filter-btn ${selectedStatus === 'pending' ? 'active' : ''}`}
          onClick={() => setSelectedStatus('pending')}
        >
          <span className="merchant-filter-label">Novos</span>
          <span className="merchant-filter-count">({stats.pending})</span>
        </button>
        <button
          className={`merchant-filter-btn ${selectedStatus === 'preparing' ? 'active' : ''}`}
          onClick={() => setSelectedStatus('preparing')}
        >
          <span className="merchant-filter-label">Preparando</span>
          <span className="merchant-filter-count">({stats.preparing})</span>
        </button>
        <button
          className={`merchant-filter-btn ${selectedStatus === 'ready' ? 'active' : ''}`}
          onClick={() => setSelectedStatus('ready')}
        >
          <span className="merchant-filter-label">Prontos</span>
          <span className="merchant-filter-count">({stats.ready})</span>
        </button>
        <button
          className={`merchant-filter-btn ${selectedStatus === 'awaiting_pickup' ? 'active' : ''}`}
          onClick={() => setSelectedStatus('awaiting_pickup')}
        >
          <span className="merchant-filter-label">Aguardando retirada</span>
          <span className="merchant-filter-count">({stats.awaiting_pickup})</span>
        </button>
        <button
          className={`merchant-filter-btn ${selectedStatus === 'cancelled' ? 'active' : ''}`}
          onClick={() => setSelectedStatus('cancelled')}
        >
          <span className="merchant-filter-label">Cancelados</span>
          <span className="merchant-filter-count">({cancelledCount})</span>
        </button>
      </div>

      <div className="orders-toolbar">
        <div className="orders-toolbar-grid">
          <label className="orders-toolbar-field">
            <span>Buscar pedido</span>
            <input
              type="text"
              value={orderSearchTerm}
              onChange={(event) => setOrderSearchTerm(event.target.value)}
              placeholder="Numero, cliente ou item"
            />
          </label>

          <label className="orders-toolbar-field">
            <span>Pagamento</span>
            <select
              value={orderPaymentFilter}
              onChange={(event) => setOrderPaymentFilter(event.target.value)}
            >
              {ORDER_PAYMENT_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          <label className="orders-toolbar-field">
            <span>Ordenar</span>
            <select
              value={orderSortMode}
              onChange={(event) => setOrderSortMode(event.target.value)}
            >
              {ORDER_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="orders-toolbar-summary">
          <strong>{filteredOrders.length}</strong>
          <span>pedido(s) exibido(s)</span>
        </div>
      </div>

      <div className="orders-container">
        {filteredOrders.length === 0 ? (
          <div className="empty-state">
            <Clock size={64} className="empty-icon" />
            <h3>Nenhum pedido</h3>
            <p>
              {statusOrders.length === 0
                ? (selectedStatus === 'all'
                  ? 'Voce ainda nao recebeu pedidos.'
                  : `Nenhum pedido com status "${MERCHANT_ORDER_STATUS[selectedStatus]?.label || selectedStatus}"`)
                : 'Nenhum pedido encontrado com os filtros atuais.'}
            </p>
          </div>
        ) : (
          <div className="orders-list">
            {filteredOrders.map((order) => {
              const orderDistanceKm = resolveOrderDeliveryDistanceKm(order);
              const paymentLabel = PAYMENT_METHOD_LABELS[getOrderPaymentFilterKey(order)] || PAYMENT_METHOD_LABELS[String(order?.payment_method || '').toLowerCase()] || 'Nao informado';
              const pickupOrder = isPickupOrder(order);
              const waitingFinancialSettlement = isOrderAwaitingFinancialSettlement(order);
              const nextStatus = getNextStatus(order.status);
              const canAdvance = canAdvanceOrderStatus(order);
              const orderCashReturnStatus = String(order?.cash_return?.status || '').trim().toLowerCase();
              const orderHasCourier = Boolean(order?.courier_id || order?.courier?.id);
              const shouldShowCashReturnPendingNote = String(order?.payment_method || '').toLowerCase() === 'cash'
                && String(order?.status || '').toLowerCase() === 'delivered'
                && orderHasCourier
                && !pickupOrder
                && !['settled', 'cancelled'].includes(orderCashReturnStatus);
              return (
              <div
                key={order.id}
                className={`order-card ${selectedOrder?.id === order.id ? 'selected' : ''}`}
                onClick={() => setSelectedOrder(order)}
              >
                <div className="order-card-header">
                  <div className="order-info">
                    <h3>Pedido #{order.order_number}</h3>
                    <p className="order-time">
                      {formatDateTime(order.created_at)}
                    </p>
                  </div>
                  <div
                    className="order-status-badge"
                    style={{
                      background: `${getStatusColor(order.status)}20`,
                      color: getStatusColor(order.status)
                    }}
                  >
                    {getMerchantOrderStatusLabel(order)}
                  </div>
                </div>

                <div className="order-card-body">
                  <div className="order-meta-chips">
                    <span className="order-meta-chip payment">{paymentLabel}</span>
                    <span className={`order-meta-chip fulfillment ${pickupOrder ? 'pickup' : 'delivery'}`}>
                      {pickupOrder ? 'Retirada' : 'Entrega'}
                    </span>
                    {Number.isFinite(orderDistanceKm) && !pickupOrder && (
                      <span className="order-meta-chip distance">{formatDistanceKm(orderDistanceKm)}</span>
                    )}
                  </div>

                  <div className="customer-info">
                    <ShoppingBag size={16} />
                    <span>{order.customer?.name || 'Cliente'}</span>
                  </div>

                  <div className="order-items-summary">
                    {order.order_items?.slice(0, 2).map((item, idx) => (
                      <p key={idx}>
                        <span className="item-qty">{item.quantity}x</span> {item.product_name}
                      </p>
                    ))}
                    {order.order_items?.length > 2 && (
                      <p className="more-items">+{order.order_items.length - 2} item(s)</p>
                    )}
                  </div>

                  <div className="order-total">
                    <strong>Total: R$ {formatCurrency(resolveDisplayedOrderTotal(order))}</strong>
                  </div>

                  {waitingFinancialSettlement && (
                    <p className="merchant-order-payment-note">
                      Aguardando transacao financeira. O pedido sera liberado para confirmacao apos o pagamento.
                    </p>
                  )}

                  {shouldShowCashReturnPendingNote && (
                    <p className="merchant-cash-return-order-note">
                      Devolucao em dinheiro pendente do motoboy para a loja.
                    </p>
                  )}

                  {canAdvance && (
                    <button
                      className="btn-advance-status"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!nextStatus) return;
                        handleUpdateStatus(order.id, nextStatus);
                      }}
                      disabled={updatingStatus}
                      style={{
                        background: getStatusColor(nextStatus),
                      }}
                    >
                      Marcar como {MERCHANT_ORDER_STATUS[nextStatus]?.label || nextStatus}
                    </button>
                  )}

                  {order.status !== 'delivered' && order.status !== 'cancelled' && (
                    <button
                      className="btn-cancel-order"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelOrder(order.id);
                      }}
                      disabled={updatingStatus}
                    >
                      Cancelar pedido
                    </button>
                  )}
                </div>
              </div>
            )})}
          </div>
        )}

        {selectedOrder && (
          <div className="order-detail-panel-overlay">
          <div className="order-detail-panel">
            <div className="panel-header">
              <h2>Detalhes do Pedido</h2>
              <button className="btn-close" onClick={() => setSelectedOrder(null)}>
                x
              </button>
            </div>

            <div className="panel-body">
              <div className="detail-section">
                <h3>Cliente</h3>
                <div className="customer-details">
                  <div className="detail-row">
                    <span>{selectedOrder.customer?.name || 'Nao informado'}</span>
                  </div>
                  <div className="detail-row">
                    <span>{selectedOrder.customer?.email || 'Nao informado'}</span>
                  </div>
                </div>
              </div>


              {selectedOrder?.fulfillment_type !== 'pickup' && selectedOrder?.delivery_address?.type !== 'pickup' && (
                <div className="detail-section">
                  <h3>Rota da entrega</h3>
                  <div className="merchant-route-map">
                    <TrackingMap
                      courierLocation={null}
                      restaurantLocation={restaurantCoords}
                      customerLocation={customerCoords}
                      route={routeData?.geometry ? { type: 'Feature', geometry: routeData.geometry, properties: {} } : null}
                      height={240}
                    />
                  </div>
                  <div className="address-display merchant-route-meta" style={{ marginTop: 12 }}>
                    {routeLoading && <p>Carregando rota...</p>}
                    {routeError && <p style={{ color: '#b91c1c' }}>{routeError}</p>}
                    {geocodeLoading && !customerCoords && <p>Localizando endereco...</p>}
                    {geocodeError && <p style={{ color: '#b91c1c' }}>{geocodeError}</p>}
                    {!customerCoords && !geocodeLoading && (
                      <p>Endereco do cliente sem coordenadas. Atualize no checkout para habilitar rota.</p>
                    )}
                    {routeData?.distance_m != null && routeData?.duration_s != null && (
                      <p>
                        Distancia: {(routeData.distance_m / 1000).toFixed(1).replace('.', ',')} km | Tempo: {Math.round(routeData.duration_s / 60)} min
                      </p>
                    )}
                  </div>
                </div>
              )}

              {shouldShowCourierOnWayToStore && (
                <div className="detail-section courier-on-way-section">
                  <h3>Status do motoboy</h3>
                  <div className={`courier-on-way-box ${selectedOrderCourierTiming?.isOverdue ? 'courier-on-way-box--overdue' : ''}`}>
                    <p className="courier-on-way-name">{selectedOrder?.courier?.name || 'Motoboy'}</p>
                    <p className="courier-on-way-text">Motoboy esta a caminho da loja.</p>
                    {selectedOrderCourierTiming ? (
                      <p className="courier-on-way-timer">
                        <span>{selectedOrderCourierTiming.instanceLabel}: {selectedOrderCourierTiming.clockLabel}</span>
                        <strong>{selectedOrderCourierTiming.countdown}</strong>
                      </p>
                    ) : null}
                  </div>
                </div>
              )}

              {!shouldShowCourierOnWayToStore && selectedOrderCourierTiming ? (
                <div className="detail-section">
                  <h3>Janela da corrida</h3>
                  <div className={`courier-on-way-box ${selectedOrderCourierTiming.isOverdue ? 'courier-on-way-box--overdue' : ''}`}>
                    <p className="courier-on-way-text">{selectedOrderCourierTiming.instanceLabel}</p>
                    <p className="courier-on-way-timer">
                      <span>{selectedOrderCourierTiming.clockLabel}</span>
                      <strong>{selectedOrderCourierTiming.countdown}</strong>
                    </p>
                  </div>
                </div>
              ) : null}


              {selectedOrder.status === 'out_for_delivery' && selectedOrder.courier && (
                <div className="detail-section">
                  <h3>Motoboy responsavel</h3>
                  <div className="address-display">
                    <p><strong>Nome:</strong> {selectedOrder.courier.name || 'Nao informado'}</p>
                    <p><strong>Telefone:</strong> {selectedOrder.courier.phone || 'Nao informado'}</p>
                    <p><strong>Email:</strong> {selectedOrder.courier.email || 'Nao informado'}</p>
                  </div>
                </div>
              )}

              
              {selectedOrder.status === 'ready'
                && (selectedOrder?.fulfillment_type || 'delivery').toLowerCase() !== 'pickup'
                && selectedOrder?.delivery_address?.type !== 'pickup' && (
                <div className="detail-section">
                  <h3>Atribuir motoboy</h3>
                  {canCallPlatformCourier ? (
                    <>
                      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 12 }}>
                        Selecione um motoboy online. Cada motoboy pode ter ate 7 pedidos ativos.
                      </p>
                      {!restaurantCoords && (
                        <p style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>
                          Loja sem coordenadas. Atualize o endereco da loja para habilitar o filtro por localidade.
                        </p>
                      )}
                      {selectedOrder.courier ? (
                        <div className="address-display" style={{ marginBottom: 12 }}>
                          <p><strong>Motoboy atribuido:</strong> {selectedOrder.courier.name || 'Nao informado'}</p>
                        </div>
                      ) : null}
                      <div className="merchant-detail-form-grid" style={{ display: 'grid', gap: 8 }}>
                        <select
                          value={selectedCourierId}
                          onChange={(e) => setSelectedCourierId(e.target.value)}
                          style={{ padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb' }}
                        >
                          <option value="">{loadingCouriers ? 'Carregando...' : 'Selecione um motoboy online'}</option>
                          {couriers.map((courier) => (
                            <option key={courier.id} value={courier.id}>
                              {courier.name || 'Motoboy'} {courier.phone ? `- ${courier.phone}` : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          className="btn-primary"
                          onClick={() => handleAssignCourier(selectedOrder.id)}
                          disabled={assigningCourier || loadingCouriers}
                        >
                          {assigningCourier ? 'Atribuindo...' : 'Atribuir motoboy'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="address-display">
                      <p>
                        Esta loja usa motoboys proprios.
                        Ative "chamar motoboy do Correfood" nas configuracoes da loja quando precisar cobertura extra.
                      </p>
                    </div>
                  )}
                </div>
              )}

{(
                selectedOrder.status === 'awaiting_pickup'
                || (
                  (selectedOrder?.fulfillment_type || 'delivery').toLowerCase() === 'pickup'
                  && selectedOrder.status === 'ready'
                )
              ) && (
                <div className="detail-section">
                  <h3>Confirmar retirada</h3>
                  <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 12 }}>
                    {(selectedOrder?.fulfillment_type || 'delivery').toLowerCase() === 'pickup'
                      ? 'Digite o codigo de retirada informado pelo cliente para registrar a retirada na loja.'
                      : 'O motoboy deve informar o codigo do pedido para liberar a entrega.'}
                  </p>
                  {(selectedOrder?.fulfillment_type || 'delivery').toLowerCase() !== 'pickup' && (
                    <p style={{ color: '#374151', fontSize: 14, marginBottom: 8 }}>
                      Codigo do pedido: <strong>{selectedOrder.order_number}</strong>
                    </p>
                  )}
                  <div className="merchant-detail-form-grid" style={{ display: 'grid', gap: 8 }}>
                    <input
                      value={pickupCode}
                      onChange={(e) => {
                        setPickupCode(e.target.value);
                        if (pickupCodeError) setPickupCodeError('');
                      }}
                      inputMode={(selectedOrder?.fulfillment_type || 'delivery').toLowerCase() === 'pickup' ? 'numeric' : 'text'}
                      maxLength={(selectedOrder?.fulfillment_type || 'delivery').toLowerCase() === 'pickup' ? 8 : undefined}
                      placeholder={(selectedOrder?.fulfillment_type || 'delivery').toLowerCase() === 'pickup'
                        ? 'Ex: 472913'
                        : 'Digite o codigo do pedido'}
                      style={{ padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <button
                      className="btn-primary"
                      onClick={() => handleConfirmPickup(selectedOrder.id)}
                      disabled={confirmingPickup}
                    >
                      {confirmingPickup ? 'Confirmando...' : 'Confirmar retirada'}
                    </button>
                  </div>
                  {pickupCodeError && (
                    <p className="pickup-code-error" role="alert">
                      {pickupCodeError}
                    </p>
                  )}
                </div>
              )}

              <div className="detail-section">
                <h3>Itens do Pedido</h3>
                <div className="detail-items">
                  {selectedOrder.order_items?.map((item) => (
                    <div key={item.id} className="detail-item">
                      <div className="item-info">
                        <span className="item-qty">{item.quantity}x</span>
                        <span className="item-name">{item.product_name}</span>
                      </div>
                      <span className="item-price">
                        R$ {formatCurrency(item.subtotal)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="detail-section">
                <h3>Resumo</h3>
                <div className="payment-summary">
                  <div className="payment-row">
                    <span>Subtotal</span>
                    <span>R$ {formatCurrency(selectedOrder.subtotal)}</span>
                  </div>
                  <div className="payment-row">
                    <span>Taxa de entrega</span>
                    <span>R$ {formatCurrency(resolveDisplayedDeliveryFee(selectedOrder))}</span>
                  </div>
                  {Number.isFinite(selectedOrderDistanceKm) && (
                    <div className="payment-row">
                      <span>Distancia da entrega</span>
                      <span>{formatDistanceKm(selectedOrderDistanceKm)}</span>
                    </div>
                  )}
                  <div className="payment-row total">
                    <strong>Total</strong>
                    <strong>R$ {formatCurrency(resolveDisplayedOrderTotal(selectedOrder))}</strong>
                  </div>
                </div>
                {selectedOrder.delivery_using_platform ? (
                  <p style={{ marginTop: 8, color: '#92400e', fontSize: 12 }}>
                    Frete da entrega Correfood nao entra no faturamento do lojista.
                  </p>
                ) : null}
                {selectedOrderPaymentMethod === 'cash' && selectedOrder.delivery_using_platform ? (
                  <p style={{ marginTop: 6, color: '#b45309', fontSize: 12 }}>
                    Em pedido cash, o lojista recebe o frete do cliente e este valor vira pendencia financeira com o Correfood.
                  </p>
                ) : null}
              </div>

              <div className="detail-section">
                <h3>Forma de pagamento</h3>
                <div className="address-display">
                  {isOrderAwaitingFinancialSettlement(selectedOrder) && (
                    <p className="merchant-order-payment-note merchant-order-payment-note-detail">
                      Aguardando transacao financeira. O botao "Marcar como Confirmado" sera liberado apos aprovacao do pagamento.
                    </p>
                  )}
                  <p>
                    <strong>Metodo:</strong>{' '}
                    {PAYMENT_METHOD_LABELS[selectedOrderPaymentMethod] || selectedOrder.payment_method || 'Nao informado'}
                  </p>
                  <p>
                    <strong>Status do pagamento:</strong> {getMerchantPaymentStatusLabel(selectedOrder)}
                  </p>
                  {selectedOrderPaymentMethod === 'voucher' && (
                    <p>
                      <strong>Voucher:</strong>{' '}
                      {`${formatVoucherIssuer(
                        selectedOrder?.voucher_issuer || selectedOrder?.voucher?.voucher_issuer || selectedOrder?.voucher?.issuer
                      )} / ${formatVoucherType(
                        selectedOrder?.voucher_type || selectedOrder?.voucher?.voucher_type || selectedOrder?.voucher?.type
                      )}`}
                    </p>
                  )}
                  {selectedOrderPaymentMethod === 'cash' && selectedOrderCashChange && (
                    <>
                      <p><strong>Levar troco:</strong> {selectedOrderCashChange.needsChange ? 'Sim' : 'Nao'}</p>
                      {selectedOrderCashChange.needsChange && (
                        <>
                          <p>
                            <strong>Nota informada:</strong>{' '}
                            R$ {formatCurrencyFromReais(selectedOrderCashChange.billAmount)}
                          </p>
                          <p>
                            <strong>Troco:</strong>{' '}
                            R$ {formatCurrencyFromReais(selectedOrderCashChange.changeDue)}
                          </p>
                        </>
                      )}
                    </>
                  )}
                  {selectedOrderPaymentMethod === 'cash'
                    && String(selectedOrder?.status || '').toLowerCase() === 'delivered'
                    && (selectedOrderIsPickup || !selectedOrderHasCourier) && (
                    <p style={{ marginTop: 6, color: '#6b7280', fontSize: 12 }}>
                      Pedido sem motoboy (retirada na loja). Nao ha codigo de devolucao de dinheiro para este caso.
                    </p>
                  )}
                </div>
              </div>

              {selectedOrderPaymentMethod === 'cash' && selectedOrderHasCourier && !selectedOrderIsPickup && String(selectedOrder?.status || '').toLowerCase() === 'delivered' && (
                <div className="detail-section">
                  <h3>Devolucao de dinheiro (motoboy -> loja)</h3>
                  <div className={`merchant-cash-return-box ${selectedOrderCashReturnStatus === 'overdue' ? 'is-overdue' : ''}`}>
                    <p>
                      <strong>Status:</strong>{' '}
                      {getCashReturnStatusLabel(selectedOrderCashReturnStatus || 'pending_return')}
                    </p>
                    <p>
                      <strong>Valor para devolver:</strong>{' '}
                      R$ {formatCurrency(selectedOrderCashReturn?.amount_total_cents ?? selectedOrder?.total ?? 0)}
                    </p>
                    <p>
                      <strong>Prazo:</strong>{' '}
                      {selectedOrderCashReturn?.due_at ? formatDateTime(selectedOrderCashReturn.due_at) : '-'}
                    </p>
                    <p>
                      <strong>Pedido:</strong> {selectedOrder?.order_number || '-'}
                    </p>
                    {selectedOrderCashReturnPending ? (
                      <button
                        type="button"
                        className="btn-primary merchant-cash-return-generate-btn"
                        onClick={() => handleGenerateCashReturnCode(selectedOrder.id)}
                        disabled={generatingCashReturnCodeId === String(selectedOrder.id)}
                      >
                        {generatingCashReturnCodeId === String(selectedOrder.id) ? 'Gerando codigo...' : 'Gerar codigo para o motoboy'}
                      </button>
                    ) : null}
                    {selectedOrderGeneratedCashReturnCode ? (
                      <div className="merchant-cash-return-code">
                        <span>Codigo de devolucao</span>
                        <strong>{selectedOrderGeneratedCashReturnCode}</strong>
                        <small>Informe este codigo ao motoboy para ele confirmar o repasse no app.</small>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}


              {selectedOrder.status === 'cancelled' && (
                <div className="detail-section">
                  <h3>Motivo do cancelamento</h3>
                  <div className="address-display">
                    <p><strong>Cancelado por:</strong> {resolveCancelledBy(selectedOrder)}</p>
                    <p><strong>Motivo do cancelamento:</strong> {resolveCancellationReason(selectedOrder) || 'Nao informado'}</p>
                    {resolveCancellationComment(selectedOrder) && (
                      <p><strong>Comentario:</strong> {resolveCancellationComment(selectedOrder)}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        )}
      </div>

      {cancelModalOpen && (
        <div className="merchant-cancel-modal-overlay" onClick={closeCancelModal}>
          <div className="merchant-cancel-modal" onClick={(event) => event.stopPropagation()}>
            <h3>Cancelar pedido</h3>
            <p>Tem certeza que deseja cancelar este pedido?</p>
            <div className="merchant-cancel-modal-actions">
              <button
                type="button"
                className="merchant-cancel-modal-back"
                onClick={closeCancelModal}
                disabled={cancelSubmitting}
              >
                Voltar
              </button>
              <button
                type="button"
                className="merchant-cancel-modal-confirm"
                onClick={confirmCancelOrder}
                disabled={cancelSubmitting || updatingStatus}
              >
                {cancelSubmitting || updatingStatus ? 'Cancelando...' : 'Confirmar cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}














